import Vue from 'vue'
import VueRouter from 'vue-router'
import {
  USER_TOKEN,
  ADMIN_EMAIL,
  ADMIN_BUSINESS_ID,
  ADMIN_TENANT,
  ADMIN_BUSINESS_NAME,
  USER_NAME,
  USER_EMAIL,
  USER_PHONE,
  USER_BUSINESS_TYPE,
  USER_WEBSITE,
  WITHDRAWAL_ACCESS,
  DEPOSIT_ACCESS,
  ROLES
} from '@/utils/constants'
import store from '../store'

Vue.use(VueRouter)
const originalPush = VueRouter.prototype.push
const originalReplace = VueRouter.prototype.replace
const tenantName = process.env.VUE_APP_TENANT_TITLE
const tenantIsTma = process.env.VUE_APP_TENANT_NAME === 'tma'
const tenantIsOval = process.env.VUE_APP_TENANT_NAME === 'oval'
const tenantIsSymmetral = process.env.VUE_APP_TENANT_NAME === 'symmetral'
const tenantIsIFT = process.env.VUE_APP_TENANT_NAME === 'ift'
const version = process.env.VUE_APP_VERSION
const isOnDevEnvironment = process.env.VUE_APP_DEV_INDICATOR === 'true'

// push
VueRouter.prototype.push = function push (location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalPush.call(this, location, onResolve, onReject)
  }
  return originalPush.call(this, location).catch((err) => err)
}
// replace
VueRouter.prototype.replace = function push (location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalReplace.call(this, location, onResolve, onReject)
  }
  return originalReplace.call(this, location).catch((err) => err)
}
const routes = [
  {
    path: '/',
    redirect: '/dashboard',
    beforeEnter: guardAppRoutes,
    component: () => import(`@/views/${version}/app/Main.vue`),
    children: [
      {
        path: '/dashboard',
        name: 'dashboard',
        beforeEnter: guardDashboardRoute,
        component: () => import(`@/views/${version}/app/Dashboard.vue`),
        meta: { title: `${tenantName} - Dashboard`, pageTitle: 'Dashboard' }
      },
      {
        path: 'dashboard/account-details',
        name: 'account-details',
        beforeEnter: guardDashboardRoute,
        component: () =>
          import(
            `@/components/${version}/accountBalanceDetails/layout/AccountBalanceDetails.vue`
          ),
        meta: {
          title: `${tenantName} - Dashboard`,
          pageTitle: 'Account Details'
        }
      },
      {
        path: 'dashboard/accounts',
        name: 'accounts',
        beforeEnter: guardDashboardRoute,
        component: () =>
          import(`@/components/${version}/accounts/layout/Accounts.vue`),
        meta: {
          title: `${tenantName} - Dashboard`,
          pageTitle: 'Accounts'
        }
      },
      {
        path: '/withdrawal',
        name: 'withdrawal',
        component: () => import(`@/views/${version}/app/Withdrawal.vue`),
        beforeEnter: guardWithdrawalRoute,
        meta: {
          title: `${tenantName}  - Withdrawal`,
          showBack: true,
          pageTitle: 'Withdrawal'
        }
      },
      {
        // this conditionally add the params
        path: '/transactions/:id?/:type?/:status?',
        name: 'transactions',
        component: () => import(`@/views/${version}/app/Transactions.vue`),
        beforeEnter: guardTransactionsRoute,
        meta: {
          title: `${tenantName} - Transactions`,
          pageTitle: 'Transactions'
        }
      },
      {
        path: '/exchange-rates',
        name: 'exchange-rates',
        beforeEnter: guardOnProd,
        component: () => import(`@/views/${version}/app/ExchangeRates.vue`),
        meta: {
          title: `${tenantName} - Exchange Rates`,
          pageTitle: 'Exchange Rates'
        }
      },
      {
        path: '/cards/:type(existing-cards)?',
        name: 'cards',
        component: () => import(`@/views/${version}/app/Cards.vue`),
        beforeEnter: guardOnProd,
        meta: { title: `${tenantName} - Cards`, pageTitle: 'Cards' }
      },
      {
        path: '/bridge',
        name: 'bridge',
        component: () => import(`@/views/${version}/app/Bridge.vue`),
        beforeEnter: guardBridge,
        meta: { title: `${tenantName} - Bridge`, pageTitle: 'Bridge' }
      },
      {
        path: '/payout',
        name: 'payout',
        beforeEnter: guardBulkPayoutRoute,
        component: () => import(`@/views/${version}/app/BulkPayout.vue`),
        meta: {
          title: `${tenantName} - Payout`,
          pageTitle: 'Payout'
        }
      },
      {
        path: '/payout/select-beneficiary',
        name: 'select-multi-beneficiary',
        beforeEnter: guardBulkPayoutRoute,
        component: () =>
          import(
            `@/components/${version}/bulkPayout/layouts/SelectMultiBeneficiary`
          ),
        meta: {
          title: `${tenantName} - Payout`,
          pageTitle: 'Payout'
        }
      },
      {
        path: '/payout/create-payout',
        name: 'create-payout',
        beforeEnter: guardBulkPayoutRoute,
        component: () =>
          import(`@/components/${version}/bulkPayout/layouts/ConfirmNewPayout`),
        meta: {
          title: `${tenantName} - Payout`,
          pageTitle: 'Payout'
        }
      },
      {
        path: '/payout/:id',
        name: 'payout-details',
        beforeEnter: guardBulkPayoutRoute,
        component: () =>
          import(`@/components/${version}/bulkPayout/layouts/payoutDetails`),
        meta: {
          title: `${tenantName} - Payout`,
          pageTitle: 'Payout'
        }
      },
      {
        path: '/terminal',
        name: 'terminal',
        component: () => import(`@/views/${version}/app/Terminal.vue`),
        beforeEnter: guardTerminal,
        meta: {
          title: `${tenantName}  - FX Transfer`,
          pageTitle: 'FX Transfer'
        }
      },
      {
        path: '/api-management',
        name: 'api-management-system',
        beforeEnter: guardAPIRoute,
        component: () => import(`@/views/${version}/app/ExternalApi.vue`),
        meta: {
          title: `${tenantName}  - ManageAPI`,
          pageTitle: 'API Management'
        }
      },
      {
        path: '/api-management/batch-details/:id',
        name: 'batch-details',
        beforeEnter: guardAPIRoute,
        component: () =>
          import('@/components/externalApi/views/settlements/BatchDetails.vue'),
        meta: {
          title: `${tenantName} - ManageAPI`,
          pageTitle: 'API Management System'
        }
      },
      {
        path: '/api-management/customer/:id',
        name: 'customer-details',
        beforeEnter: guardAPIRoute,
        component: () =>
          import(
            `@/components/${version}/externalApi/views/customers/CustomerDetails.vue`
          ),
        meta: {
          title: `${tenantName}  - ManageAPI`,
          pageTitle: 'API Management'
        }
      },
      {
        path: '/api-management/yield/:id',
        name: 'Yield Profile Details',
        beforeEnter: guardAPIRoute,
        component: () =>
          import(
            '@/components/externalApi/views/yieldOfferings/YieldProfileDetails.vue'
          ),
        meta: {
          title: `${tenantName}  - ManageAPI`,
          pageTitle: 'API Management System'
        }
      },
      {
        path: '/receive-payment',
        name: 'payment-links',
        component: () => import(`@/views/${version}/app/ReceivePayment.vue`),
        beforeEnter: guardPaymentLink,
        meta: {
          title: `${tenantName} - Receive Payment`,
          pageTitle: 'Payment Links'
        }
      },
      {
        path: '/payment-link',
        name: 'Payment Link',
        component: () => import('@/components/receivePayment/PaymentLink.vue'),
        beforeEnter: guardPaymentLink,
        meta: {
          title: `${tenantName} - Payment Link`,
          showBack: true,
          pageTitle: 'Payment Links'
        }
      },
      {
        path: '/payment-link-details/:id',
        name: 'payment-link-details',
        beforeEnter: guardPaymentLink,
        component: () =>
          import('@/components/v2/paymentLink/PaymentLinkDetails.vue'),
        meta: {
          title: `${tenantName}- Payment Link Details`,
          pageTitle: 'Payment Links'
        }
      },
      {
        path: '/customers-view',
        name: 'customers-view',
        component: () =>
          import('@/components/receivePayment/customersView/CustomersView.vue'),
        meta: {
          title: `${tenantName}- Payment Link Details`,
          pageTitle: 'Payment Links'
        }
      },
      {
        path: '/settings',
        name: 'settings',
        beforeEnter: guardKyc,
        component: () => import(`@/views/${version}/app/Settings.vue`),
        meta: { title: `${tenantName}  - Settings`, pageTitle: 'Settings' }
      },
      {
        path: '/settings/profile',
        name: 'profile-settings',
        beforeEnter: guardKyc,
        component: () => import('@/components/settings/Profile.vue'),
        meta: {
          title: `${tenantName}  - Settings`,
          activeTitle: 'Profile',
          pageTitle: 'Settings'
        }
      },
      {
        path: '/settings/contracts',
        name: 'contract-settings',
        beforeEnter: guardKyc,
        component: () =>
          import(`@/components/${version}/settings/Contract.vue`),
        meta: { title: `${tenantName}  - Settings`, pageTitle: 'Settings' }
      },
      {
        path: '/settings/business-overview',
        name: 'business-overview-settings',
        beforeEnter: guardKyc,
        component: () =>
          import(`@/components/${version}/settings/BusinessOverview.vue`),
        meta: { title: `${tenantName}  - Settings`, pageTitle: 'Settings' }
      },
      {
        path: '/settings/withdrawal-method',
        name: 'withdrawal-method-settings',
        beforeEnter: guardTransactionsRoute,
        component: () =>
          import(`@/components/${version}/settings/WithdrawalMethod.vue`),
        meta: {
          title: `${tenantName}  - Settings`,
          pageTitle: 'Settings'
        }
      },
      {
        path: '/settings/security',
        name: 'security-settings',
        beforeEnter: guardKyc,
        component: () =>
          import(`@/components/${version}/settings/Security.vue`),
        meta: { title: `${tenantName}  - Settings`, pageTitle: 'Settings' }
      },
      {
        path: '/settings/api-keys-and-webhooks',
        name: 'api-keys-and-webhooks-settings',
        beforeEnter: guardKyc,
        component: () => import('@/components/settings/APIKeysAndWebhooks.vue'),
        meta: {
          title: `${tenantName}  - Settings`,
          pageTitle: 'Settings'
        }
      },
      {
        path: '/settings/beneficiaries',
        name: 'beneficiaries-settings',
        beforeEnter: guardTransactionsRoute,
        component: () =>
          import(`@/components/${version}/settings/Beneficiaries.vue`),
        meta: {
          title: `${tenantName}  - Settings`,
          pageTitle: 'Settings'
        }
      },
      {
        path: '/settings/user-management',
        name: 'user-management-settings',
        beforeEnter: guardUserManagementRoute,
        component: () =>
          import(`@/components/${version}/settings/UserManagement.vue`),
        meta: {
          title: `${tenantName}  - Settings`,
          pageTitle: 'Settings'
        }
      },
      {
        path: '/settings/api-keys',
        name: 'api-keys-settings',
        beforeEnter: guardKyc,
        component: () =>
          import('@/components/v2/settings/apiSettings/index.vue'),
        meta: {
          title: `${tenantName}  - Settings`,
          pageTitle: 'Settings'
        }
      },
      {
        path: '/settings/wallet-address',
        name: 'wallet-address-settings',
        beforeEnter: guardBridge,
        component: () => import('@/components/settings/WalletAddress.vue'),
        meta: {
          title: `${tenantName}  - Settings`,
          activeTitle: 'Wallet Address',
          pageTitle: 'Settings'
        }
      },
      {
        path: '/settings/ip-whitelisting',
        name: 'ip-whitelisting',
        beforeEnter: guardKyc,
        component: () => import('@/components/v2/settings/IPWhitelisting.vue'),
        meta: { title: `${tenantName}  - Settings`, pageTitle: 'Settings' }
      }
    ]
  },
  {
    path: '/',
    component: () => import(`@/views/${version}/auth/Main.vue`),
    children: [
      {
        path: '/login',
        name: 'login',
        component: () => import(`@/views/${version}/auth/Login.vue`),
        meta: { title: `${tenantName} - Login` }
      },
      {
        path: '/sign-up',
        name: 'signUp',
        component: () => import(`@/views/${version}/auth/SignUp.vue`),
        meta: { title: `${tenantName}  - Signup` }
      },
      {
        path: '/sign-up/invite/:requestID',
        name: 'signUpInvite',
        component: () => import(`@/views/${version}/auth/SignUp.vue`),
        meta: { title: `${tenantName}  - Signup` }
      },
      {
        path: '/forgot-password',
        name: 'forgotPassword',
        component: () => import(`@/views/${version}/auth/ForgotPassword.vue`),
        meta: { title: `${tenantName}  - Forgot Password` }
      },
      {
        path: '/recover-password',
        name: 'recoverPassword',
        component: () => import(`@/views/${version}/auth/RecoverPassword.vue`),
        meta: { title: `${tenantName}  - Recover Password` }
      },
      {
        path: '/account-setup',
        name: 'accountSetup',
        component: () => import('@/components/auth/PtWidget.vue'),
        meta: { title: `${tenantName}  - Account Setup` },
        beforeEnter: guardRoute
      },
      {
        path: '/submit-kyc',
        beforeEnter: guardRoute,
        component: () => import('@/views/v2/app/CompleteKyc.vue'),
        children: [
          {
            path: '',
            name: 'submit-kyc',
            beforeEnter: guardRoute,
            component: () => import('@/components/v2/completeKycForm/Main.vue'),
            meta: { title: `${tenantName} - Submit KYC` }
          },
          {
            path: 'business-information',
            name: 'business-information',
            beforeEnter: guardRoute,
            component: () =>
              import(
                '@/components/v2/completeKycForm/business/BusinessInformation.vue'
              ),
            meta: { title: `${tenantName} - Submit KYC | Business Information` }
          },
          {
            path: 'director-information',
            name: 'director-information',
            beforeEnter: guardRoute,
            component: () =>
              import(
                '@/components/v2/completeKycForm/director/DirectorInformation.vue'
              ),
            meta: { title: `${tenantName} - Submit KYC | Director Information` }
          },
          {
            path: 'upload-document',
            name: 'upload-document',
            beforeEnter: guardRoute,
            component: () =>
              import(
                '@/components/v2/completeKycForm/document/UploadDocument.vue'
              ),
            meta: { title: `${tenantName} - Submit KYC | Upload Documents` }
          },
          {
            path: 'kyc-completed',
            name: 'kyc-completed',
            beforeEnter: guardRoute,
            component: () =>
              import('@/components/v2/completeKycForm/KycCompleted.vue'),
            meta: { title: `${tenantName} - KYC Completed` }
          }
        ]
      }
    ]
  },
  {
    path: '/verify-email',
    name: 'verifiedEmail',
    component: () => import(`@/views/${version}/auth/VerifiedEmail.vue`)
  },
  {
    path: '/verify-user',
    name: 'verifyUser',
    component: () => import(`@/views/${version}/auth/VerifyUser.vue`)
  },
  {
    path: '/admin-login/:details',
    name: 'adminLogin',
    beforeEnter: guardAdminLogin,
    component: () => import(`@/views/${version}/auth/AdminLogin.vue`)
  },
  {
    path: '/system-error',
    name: 'systemErrorPage',
    component: () => import(`@/views/${version}/common/SystemErrorPage.vue`),
    meta: { title: `${tenantName}  - system error` }
  },
  {
    path: '*',
    name: '404ErrorPage',
    component: () => import(`@/views/${version}/common/404Page.vue`),
    meta: { title: `${tenantName}  - 404` }
  },
  {
    path: '/manage-webhooks',
    name: 'Manage Webhooks',
    beforeEnter: guardAPIRoute,
    component: () => import(`@/views/${version}/app/ManageWebhooks.vue`),
    meta: { title: `${tenantName}  - manage webhooks` }
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

const DEFAULT_TITLE = process.env.VUE_APP_TENANT_TITLE
!isOnDevEnvironment &&
  router.afterEach((to, from) => {
    Vue.nextTick(() => {
      document.title = to.meta.title || DEFAULT_TITLE
    })
  })

function guardRoute (to, from, next) {
  const token = localStorage.getItem(USER_TOKEN)
  if (token) {
    next()
  } else {
    next('/login')
  }
}
function guardWithdrawalRoute (to, from, next) {
  const userRole = store.state.auth.user.kind
  const hasKyc = store.state.auth.business.completedKyc
  if (
    WITHDRAWAL_ACCESS.includes(userRole) &&
    process.env.VUE_APP_DEPOSIT_WITHDRAW_ACCESS === 'true' &&
    hasKyc
  ) {
    next()
  } else {
    next('/dashboard')
  }
}
function guardTransactionsRoute (to, from, next) {
  const hasKyc = store.state.auth.business.completedKyc
  const developerAccess = ROLES.find(
    (item) => item.label === 'developer_access'
  )
  const currentUser = store.state.auth.user.kind
  const hasDeveloperAccess = currentUser === developerAccess.value

  if (hasKyc && !hasDeveloperAccess) {
    next()
  } else {
    next('/dashboard')
  }
}
function guardUserManagementRoute (to, from, next) {
  const hasKyc = store.state.auth.business.completedKyc
  const owner = ROLES.find((role) => role.label === 'owner')
  const currentUser = store.state.auth.user.kind
  const isOwner = currentUser === owner.value

  if (hasKyc && isOwner) {
    next()
  } else {
    next('settings/business-overview')
  }
}
function guardKyc (to, from, next) {
  const hasKyc = store.state.auth.business.completedKyc
  if (hasKyc) {
    next()
  } else {
    next('/dashboard')
  }
}
function guardPaymentLink (to, from, next) {
  const hasKyc = store.state.auth.business.completedKyc
  const developerAccess = ROLES.find(
    (item) => item.label === 'developer_access'
  )
  const currentUser = store.state.auth.user.kind
  const hasDeveloperAccess = currentUser === developerAccess.value

  if (hasKyc && !hasDeveloperAccess) {
    next()
  } else {
    next('/dashboard')
  }
}
function guardBridge (to, from, next) {
  const hasKyc = store.state.auth.business.completedKyc
  const developerAccess = ROLES.find(
    (item) => item.label === 'developer_access'
  )
  const currentUser = store.state.auth.user.kind
  const hasDeveloperAccess = currentUser === developerAccess.value

  if (hasKyc && tenantIsOval && !hasDeveloperAccess) {
    next()
  } else {
    next('/dashboard')
  }
}
function guardTerminal (to, from, next) {
  const hasKyc = store.state.auth.business.completedKyc
  const currentUser = store.state.auth.user.kind
  const hasDepositAccess = DEPOSIT_ACCESS.includes(currentUser)

  if (
    hasKyc &&
    !tenantIsTma &&
    hasDepositAccess &&
    !tenantIsSymmetral &&
    !tenantIsIFT
  ) {
    next()
  } else {
    next('/dashboard')
  }
}
function guardOnProd (to, from, next) {
  const isOnProdEnvironment = process.env.NODE_ENV === 'production'
  if (!isOnProdEnvironment) {
    next()
  } else {
    next('/dashboard')
  }
}
export function guardAPIRoute (to, from, next) {
  const hasKyc = store.state.auth.business.completedKyc
  const hasApiAccess =
    (!!store.state.auth.businessConfig.has_api_config ||
      localStorage.getItem(ADMIN_BUSINESS_NAME)?.length > 0) &&
    process.env.VUE_APP_API_ACCESS === 'true' &&
    hasKyc
  if (hasApiAccess && !tenantIsTma && !tenantIsSymmetral && !tenantIsIFT) {
    next()
  } else {
    next('/dashboard')
  }
}
function guardDashboardRoute (to, from, next) {
  const hasAccess = process.env.VUE_APP_DASHBOARD_ACCESS === 'true'
  if (hasAccess) {
    next()
  } else {
    next('/terminal')
  }
}
function guardBulkPayoutRoute (to, from, next) {
  const hasBulkPayoutAccess =
    store.state.auth.serviceOfferings.bulk_payout?.is_enabled ||
    localStorage.getItem(ADMIN_BUSINESS_NAME)?.length > 0
  const developerAccess = ROLES.find(
    (item) => item.label === 'developer_access'
  )
  const currentUser = store.state.auth.user.kind
  const hasDeveloperAccess = currentUser === developerAccess.value

  if (hasBulkPayoutAccess && !hasDeveloperAccess && !tenantIsSymmetral) {
    next()
  } else {
    next('/dashboard')
  }
}
export function guardAppRoutes (to, from, next) {
  const token = localStorage.getItem(USER_TOKEN)
  const hasKyc = store.state.auth.business.completedKyc
  const isManualKyc = store.state.auth.business.kycType === 'manual'
  const isThirdPartyKyc = store.state.auth.business.kycType === 'third_party'
  const kycInReview =
    store.state.kyc.kycDetails?.reviewStatus === 'pending_review'
  if (token && (hasKyc || kycInReview)) {
    next()
  } else if (token && !hasKyc && isManualKyc) {
    next('/submit-kyc')
  } else if (token && !hasKyc && isThirdPartyKyc) {
    next('/account-setup')
  } else {
    next('/login')
  }
}
export function guardAdminLogin (to, from, next) {
  const params = new URLSearchParams(to.query)
  const businessID = params.get('businessID') || ''
  const adminBusinessName = params.get('adminBusinessName') || ''
  const adminEmail = params.get('adminEmail') || ''
  const adminTenant = params.get('adminTenant') || ''
  const userName = params.get('userName') || ''
  const userEmail = params.get('userEmail') || ''
  const userNumber = params.get('userNumber') || ''
  const userBusType = params.get('userBusType') || ''
  const userWebsite = params.get('userWebsite') || ''

  localStorage.setItem(ADMIN_EMAIL, adminEmail)
  localStorage.setItem(ADMIN_BUSINESS_NAME, adminBusinessName)
  localStorage.setItem(ADMIN_TENANT, adminTenant)
  localStorage.setItem(ADMIN_BUSINESS_ID, businessID)
  localStorage.setItem(USER_NAME, userName)
  localStorage.setItem(USER_EMAIL, userEmail)
  localStorage.setItem(USER_PHONE, userNumber)
  localStorage.setItem(USER_BUSINESS_TYPE, userBusType)
  localStorage.setItem(USER_WEBSITE, userWebsite)

  next()
}

export default router
